import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/shared/PageHero";
import { SectionLabel } from "@/components/shared/SectionLabel";
import { RevealText } from "@/components/shared/RevealText";
import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { ClientLogos } from "@/components/shared/ClientLogos";
import { CTASection } from "@/components/shared/CTASection";
import { Target, UsersThree, ChatCircleText, Handshake } from "@phosphor-icons/react";
import { CLIENT_LOGOS } from "@/data/siteData";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1495433923968-85c6751d2df6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const LOOK_FOR = [
  {
    icon: Target,
    title: "Understanding of Staffing Requirements",
    description: "A clear understanding of the role, subject and experience your school is looking for.",
  },
  {
    icon: UsersThree,
    title: "Suitable Candidate Identification",
    description: "Candidates identified against the specific requirements shared by your school.",
  },
  {
    icon: ChatCircleText,
    title: "Professional Communication",
    description: "Clear, professional communication throughout the recruitment process.",
  },
  {
    icon: Handshake,
    title: "Recruitment Support",
    description: "Ongoing support as a recruitment partner for your school's staffing needs.",
  },
];

export default function Clients() {
  return (
    <Layout
      title="Clients | Mithra Jobs"
      description="Mithra Jobs partners with schools looking for dependable teacher recruitment support and teaching talent."
    >
      <PageHero
        label="Clients"
        heading="Partnering with Schools"
        description="Mithra Jobs supports schools with their teacher recruitment requirements, working as a dependable recruitment partner."
        image={HERO_IMAGE}
        imageAlt="Modern school corridor"
      />

      <section className="py-24 px-6 bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-12">
            <SectionLabel>Our Clients</SectionLabel>
            <RevealText
              as="h2"
              text="Schools We Support"
              className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight leading-tight"
            />
            <p className="mt-5 text-slate leading-relaxed">
              A selection of the schools and institutions Mithra Jobs has partnered with for teacher recruitment.
            </p>
          </div>
          <ClientLogos logos={CLIENT_LOGOS} />
        </div>
      </section>

      <section className="py-24 px-6 bg-white border-t border-navy/10">
        <div className="max-w-7xl mx-auto">
          <SectionLabel>What Schools Look For</SectionLabel>
          <RevealText
            as="h2"
            text="What Schools Look for in a Recruitment Partner"
            className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight leading-tight mb-14 max-w-3xl"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {LOOK_FOR.map((item, i) => (
              <AnimatedSection
                key={item.title}
                delay={i * 0.08}
                data-testid={`look-for-card-${i}`}
                className="border border-navy/10 rounded-2xl p-8 bg-cream"
              >
                <item.icon size={26} weight="duotone" className="text-accent mb-5" />
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-sm text-slate leading-relaxed">{item.description}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        heading="Discuss Your School's Requirement"
        description="Tell us about your school's teacher recruitment needs and our team will get in touch."
        primaryLabel="Discuss Your School's Requirement"
        primaryTo="/contact-us#enquiry"
        testIdPrefix="clients-cta"
      />
    </Layout>
  );
}
